"use client";
import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";

/**
 * HoverBorderGradient — borda animada com gradiente que gira em volta do
 * elemento e "acende" quando o usuário passa o mouse.
 *
 * Adaptado da Aceternity UI para o tema da Linkia:
 *  - fundo interno: #131313 (mesma cor dos cards do chat)
 *  - highlight no hover: verde da marca (#22C55E)
 *
 * Props:
 *  - as?: tag/componente do container (default "button")
 *  - containerClassName?: classes para a casca externa (defina o rounded aqui)
 *  - className?: classes para o miolo (onde fica o conteúdo)
 *  - innerClassName?: classes para a "máscara" interna que esconde o miolo
 *      da animação (útil quando você quer trocar o bg interno).
 *  - duration?: duração da rotação do gradiente em segundos (default 1)
 *  - clockwise?: sentido horário da rotação (default true)
 *  - highlight?: cor central do brilho do hover (default verde Linkia)
 */
export function HoverBorderGradient({
    children,
    containerClassName,
    className,
    innerClassName,
    as: Tag = "button",
    duration = 1,
    clockwise = true,
    highlight = "#22C55E",
    ...props
}) {
    const [hovered, setHovered] = useState(false);
    const [direction, setDirection] = useState("TOP");

    const rotateDirection = (currentDirection) => {
        const directions = ["TOP", "LEFT", "BOTTOM", "RIGHT"];
        const currentIndex = directions.indexOf(currentDirection);
        const nextIndex = clockwise
            ? (currentIndex - 1 + directions.length) % directions.length
            : (currentIndex + 1) % directions.length;
        return directions[nextIndex];
    };

    const movingMap = {
        TOP: "radial-gradient(20.7% 50% at 50% 0%, hsl(0, 0%, 100%) 0%, rgba(255, 255, 255, 0) 100%)",
        LEFT: "radial-gradient(16.6% 43.1% at 0% 50%, hsl(0, 0%, 100%) 0%, rgba(255, 255, 255, 0) 100%)",
        BOTTOM:
            "radial-gradient(20.7% 50% at 50% 100%, hsl(0, 0%, 100%) 0%, rgba(255, 255, 255, 0) 100%)",
        RIGHT:
            "radial-gradient(16.2% 41.2% at 100% 50%, hsl(0, 0%, 100%) 0%, rgba(255, 255, 255, 0) 100%)",
    };

    const highlightGradient = `radial-gradient(75% 181.15942028985506% at 50% 50%, ${highlight} 0%, rgba(255, 255, 255, 0) 100%)`;

    useEffect(() => {
        if (!hovered) {
            const interval = setInterval(() => {
                setDirection((prevState) => rotateDirection(prevState));
            }, duration * 1000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hovered, duration, clockwise]);

    return (
        <Tag
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={cn(
                "relative flex rounded-full border content-center bg-black/20 hover:bg-black/10 transition duration-500 dark:bg-white/20 items-center flex-col flex-nowrap gap-10 h-min justify-center overflow-visible p-px decoration-clone w-fit",
                containerClassName
            )}
            {...props}
        >
            <div
                className={cn(
                    "w-auto text-white relative z-10 bg-[#131313] px-4 py-2 rounded-[inherit]",
                    className
                )}
            >
                {children}
            </div>
            <motion.div
                className={cn(
                    "flex-none inset-0 overflow-hidden absolute z-0 rounded-[inherit] pointer-events-none"
                )}
                style={{
                    filter: "blur(2px)",
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                }}
                initial={{ background: movingMap[direction] }}
                animate={{
                    background: hovered
                        ? [movingMap[direction], highlightGradient]
                        : movingMap[direction],
                }}
                transition={{ ease: "linear", duration: duration ?? 1 }}
            />
            <div
                className={cn(
                    "bg-[#131313] absolute z-[1] flex-none inset-[1.5px] rounded-[inherit] pointer-events-none",
                    innerClassName
                )}
            />
        </Tag>
    );
}

export default HoverBorderGradient;
